import { GoogleMap, MapDirectionsService } from '@angular/google-maps';
import { HttpClient } from '@angular/common/http';
import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { Observable, of, interval, Subscription } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { grayGoogleMap } from './g-map-styles';
import { isInPast, getTwoDatesRange } from './../../../helpers/date-formatter'

interface ProTrackingInterface {
    active_from: number;
    progress: ProTrackingProgressStepInterface[];
    team: {
        name: string;
        photo: string;
    };
    units: {
        name: string;
        photo: string;
    }[];
    main_unit_location: {
        lat: number;
        lng: number;
    };
    booking_address_coordinates: {
        lat: number;
        lng: number;
    };
    eta: number;
    actions: ProTrackingActions[];
}

interface ProTrackingActions {
    endpoint: string;
    type: 'call_cs' | 'call_unit' | 'rate' | 'direct_call';
    deep_link: string;
    title: string;
    active_to: 1;
}

interface ProTrackingProgressStepInterface {
    type: 'on_previous_job' | 'on_the_way' | 'started' | 'finished';
    title: string;
    sort: number;
    active: boolean;
    icon_url: string;
}

@Component({
    selector: 'serviceos-ng-pro-tracking',
    templateUrl: './pro-tracking.component.html',
    styleUrls: ['./pro-tracking.component.scss'],
})
export class ProTrackingComponent implements OnInit {
    @Input()
    proTrackingData: ProTrackingInterface | null = null;

    // public debug = {
    //     active_from: 1695882600,
    //     progress: [
    //       {
    //         type: "on_previous_job",
    //         title: "At another job",
    //         sort: 100,
    //         active: false,
    //         icon_url: "https://files.dxr.cloud/5HTwJnG5sALAM46RCJfR3Pst4ZAbPMKGUf3LV9lGK3eVZFFgG3ty2IIjmlqE"
    //       },
    //       {
    //         type: "on_the_way",
    //         title: "On the way",
    //         sort: 200,
    //         active: false,
    //         icon_url: "https://files.dxr.cloud/y8EloC7C16EkIlKMlAOEeX5pjssOqWpRFoA0a8d2C1zkn3OMgCHycplD4DKU"
    //       },
    //       {
    //         type: "started",
    //         title: "On the job",
    //         sort: 300,
    //         active: false,
    //         icon_url: "https://files.dxr.cloud/VKH06DaWwpgyzkIrKewM3HexI2yG3MoOTQ11KNVPzqleVv9gxwDMZsyIHpWE"
    //       },
    //       {
    //         type: "finished",
    //         title: "Job done",
    //         sort: 400,
    //         active: false,
    //         icon_url: "https://files.dxr.cloud/eUZ4uOXdhlafVWiPNSeMpGJwZMoxEdz1LbAOts1awHsq1Z4s7s8NJmVnxRYI"
    //       }
    //     ],
    //     team: null,
    //     units: [
    //       {
    //         name: "Maria",
    //         photo: "https://files.dxr.cloud/2s09gBKmzLzDipOYjjFp3OWYh4mjCqjGOQaLPZrk7ks363Dhu4Yt2GxdQpYH"
    //       }
    //     ],
    //     main_unit_location: null,
    //     booking_address_coordinates: {
    //       lat: 0,
    //       lng: 0
    //     },
    //     eta: null,
    //     actions: [
    //         {
    //             endpoint: "/bookings/8092585/call_cs",
    //             type: "call_cs",
    //             deep_link: "action=call_cs",
    //             title: "Call Support",
    //             active_to: 1
    //         },
    //         {
    //             endpoint: null,
    //             type: "direct_call",
    //             deep_link: "action=direct_call&fs_payload[phone]=1234567890",
    //             title: "Call Profesional",
    //             active_to: 1
    //         }
    //     ]
    // }

    @Input()
    googleApiKey: string | null = null;

    directionsResults: any;
    googleMapLoaded = false;
    googleOptionsMap: google.maps.MapOptions = {};
    googleOptionsClientMarker: google.maps.MarkerOptions = {};
    googleOptionsProMarker: google.maps.MarkerOptions = {};
    googleOptionsProMarkerDot: google.maps.MarkerOptions = {};
    googleOptionsPath: google.maps.DirectionsRendererOptions = {};

    trackingStatus:
        | 'on_previous_job'
        | 'on_the_way'
        | 'started'
        | 'finished'
        | 'disable' = 'disable';

    @Input()
    googleMapOptions: {
        pathColor: string;
        clientMarkerColor: string;
        proMarkerColor: string;
    } = {
        pathColor: '#80CBFF',
        clientMarkerColor: '#1155CC',
        proMarkerColor: '#1155CC',
    };

    unitData: {
        name: string;
        photo: string;
    } | null = null;

    autoUpdate = false;
    autoUpdateSubscription: Subscription | null = null;
    eta_message = '';

    @Output()
    OnUpdateData = new EventEmitter();

    @Output()
    onDeepLink = new EventEmitter();

    @Input()
    showLocalLoader = false;

    @Input()
    texts = {
        map_wait_message:
            'The location of your professional(s) will be available 30 minutes before your appointment. Right now, they’re busy, so please, check in a bit. :)',
        map_wait_link: 'Check',
        action_title: 'Your professional(s):',
        map_eta_start_text: 'Approx.',
        map_eta_end_text: 'mins away'
    };

    constructor(
        private httpClient: HttpClient,
        public mapDirectionsService: MapDirectionsService
    ) {}

    ngOnInit(): void {
    }

    loadGoogleMap() {
        if (!this.proTrackingData) {
            return;
        }

        if (this.proTrackingData?.eta) {
            this.eta_message = this.generateEtaMessage(this.proTrackingData.eta);
        } else {
            this.eta_message = '';
        }

        if (
            !this.googleMapLoaded &&
            this.googleApiKey &&
            !window?.google?.maps
        ) {
            this.httpClient
                .jsonp(
                    `https://maps.googleapis.com/maps/api/js?key=${this.googleApiKey}`,
                    'callback'
                )
                .pipe(
                    take(1),
                    map(() => true),
                    catchError(() => of(false))
                )
                .subscribe((isLoader) => {
                    if (isLoader && this.proTrackingData) {
                        this.initializeGoogleMap();
                    }
                });
        } else {
            if (!this.googleMapLoaded) {
                this.initializeGoogleMap();
            } else {
                this.updateGoogleMap();
            }
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.proTrackingData?.currentValue) {
            // load google map only if proTrackingData is available
            this.loadGoogleMap();
        } else {
            this.clearAutoUpdateTrackingInfo();
        }
    }

    ngAfterViewChecked(): void {
        //Called after every check of the component's view. Applies to components only.
        //Add 'implements AfterViewChecked' to the class.
    }

    generateEtaMessage(eta) {

        const start = new Date();
        const end = new Date(eta * 1000);

        const estimatedTime = getTwoDatesRange(start, end);

        if (estimatedTime) {
            this.eta_message = this.texts.map_eta_start_text + ' ' + estimatedTime + ' ' + this.texts.map_eta_end_text;
        }
        
        return this.eta_message;
    }

    deepLink(action) {
        this.onDeepLink.emit(action);
    }

    updateData() {
        this.OnUpdateData.emit();
    }

    autoUpdateTrackingInfo() {
        if (this.autoUpdate == false) {
            this.autoUpdate = true;
            this.autoUpdateSubscription = interval(30000).subscribe(() => {
                this.OnUpdateData.emit();
            });
        }
    }

    clearAutoUpdateTrackingInfo() {
        this.autoUpdateSubscription?.unsubscribe();
    }

    ngOnDestroy(): void {
        this.clearAutoUpdateTrackingInfo();
    }

    public makeTheRoute(start_loc, end_loc) {
        const request: google.maps.DirectionsRequest = {
            destination: end_loc,
            origin: start_loc,
            travelMode: google.maps.TravelMode.DRIVING,
        };

        return this.mapDirectionsService
            .route(request)
            .pipe(
                take(1),
                map((response) => response.result)
            )
            .subscribe((res) => {
                this.directionsResults = res;
            });
    }

    public updateGoogleMap() {
        if (!this.proTrackingData) {
            return;
        }

        const lastActiveStep = [...this.proTrackingData.progress]
            .reverse()
            .find((step) => {
                return step.active;
            });

        this.trackingStatus = lastActiveStep?.type || 'disable';

        if (
            this.trackingStatus === 'on_the_way' &&
            this.proTrackingData.main_unit_location
        ) {
            this.makeTheRoute(
                this.proTrackingData.main_unit_location,
                this.proTrackingData.booking_address_coordinates
            );
        }

        // start autoUpdateTrackingInfo() if active_from is past or status is !== finished/disable
        if (
            (this.trackingStatus !== 'finished' && this.trackingStatus !== 'disable') || 
            (isInPast(this.proTrackingData?.active_from) && this.trackingStatus === 'disable')
        ) {
            this.autoUpdateTrackingInfo();
        }

        if (this.trackingStatus === 'finished' || (this.trackingStatus === 'disable' && !isInPast(this.proTrackingData.active_from))) {
            this.clearAutoUpdateTrackingInfo();
        }
    }

    public initializeGoogleMap() {
        if (!this.proTrackingData) {
            return;
        }

        if (this.proTrackingData.team) {
            this.unitData = this.proTrackingData.team;
        } else if (this.proTrackingData?.units[0]) {
            this.unitData = this.proTrackingData.units[0];
        }

        const lastActiveStep = [...this.proTrackingData.progress]
            .reverse()
            .find((step) => {
                return step.active;
            });

        this.trackingStatus = lastActiveStep?.type || 'disable';

        this.googleOptionsClientMarker = {
            draggable: false,
            icon: {
                path:
                    'M 18.488 8.264 L 18.488 8.264 L 10.736 0.513 C 10.053368720647782 -0.1696062578493468 8.946631279352218 -0.16960625784934658 8.264000000000001 0.5130000000000005 L 8.264000000000001 0.513 L 0.518 8.259 L 0.51 8.267 C 0.021727549857848694 8.75795870574519 -0.13071830058656086 9.49085466188303 0.12128202168760582 10.135793631002224 C 0.37328234396177257 10.780732600121418 0.9822210242147555 11.216128860478202 1.6740000000000002 11.245999999999999 L 2.037 11.245999999999999 L 2.037 16.945999999999998 C 2.0356722983759945 17.489429079739406 2.2502761728525753 18.0111279702728 2.6336008802756634 18.396328043192085 C 3.0169255876987515 18.781528116111375 3.5375709123642336 18.998675534304887 4.081000000000001 19 L 7.116 19 C 7.423622605655751 19 7.672999999999999 18.750622605655753 7.673 18.443 L 7.673 13.972 C 7.6735511566613885 13.455842227848365 8.091842227848366 13.037551156661388 8.608 13.036999999999999 L 10.395 13.036999999999999 C 10.911157772151634 13.037551156661388 11.32944884333861 13.455842227848365 11.33 13.972 L 11.33 18.442999999999998 C 11.33 18.75062260565575 11.579377394344249 18.999999999999996 11.887 18.999999999999996 L 14.919 18.999999999999996 C 16.048680023316592 18.99889854478621 16.9643465390756 18.08367935023666 16.966 16.953999999999997 L 16.966 11.253999999999998 L 17.252000000000002 11.253999999999998 C 17.959133142490312 11.25381441087194 18.596581596032156 10.827839441943617 18.867296685040156 10.174577889244445 C 19.138011774048156 9.52131633654527 18.988723634542776 8.769313506444615 18.489000000000004 8.268999999999998 Z',
                fillColor: this.googleMapOptions.clientMarkerColor,
                fillOpacity: 1,
                strokeWeight: 0,
                anchor: new google.maps.Point(10, 10),
                scale: 1,
            },
        };

        this.googleOptionsProMarker = {
            draggable: false,
            icon: {
                path:
                    'M 19.13795 39.77039 C 18.76877 40.07654 18.23613 40.07654 17.86695 39.77039 L 12.668409999999998 35.46152 C 5.330309999999998 33.17201 -1.7763568394002505e-15 26.280720000000002 -1.7763568394002505e-15 18.13513 C -1.7763568394002505e-15 8.11937 8.058869999999999 0 18 0 C 27.94113 0 36 8.11937 36 18.13513 C 36 25.63678 31.47915 32.074600000000004 25.03264 34.83391 C 24.98194 34.90636000000001 24.92117 34.972840000000005 24.85095 35.03108 z M 28.69962 27.81828 C 27.15567 27.12331 25.57572 26.51255 23.96662 25.98865 C 23.00462 25.64509 22.01262 25.29246 21.10362 24.95394 L 21.10362 22.3717 C 22.37714 21.52473 23.112679999999997 20.063670000000002 23.038619999999998 18.52806 C 23.27347 18.40239 23.465169999999997 18.2082 23.58862 17.97091 C 23.76796 17.61609 23.85859 17.2225 23.852619999999998 16.824370000000002 C 23.90252 16.19527 23.640479999999997 15.582010000000002 23.15262 15.186160000000001 L 23.174619999999997 15.11664 C 23.710939999999997 13.75324 23.883329999999997 12.27205 23.674619999999997 10.820630000000001 C 23.25162 9.085700000000001 20.874619999999997 8.46911 18.974619999999998 8.46911 C 17.24262 8.46911 15.119619999999998 8.97891 14.424619999999997 10.37632 C 13.804749999999997 10.2791 13.180179999999996 10.52029 12.783619999999997 11.01004 C 11.988619999999997 12.06691 12.544619999999997 14.05573 12.842619999999997 15.1247 L 12.857619999999997 15.179110000000001 C 12.361579999999996 15.573000000000002 12.094589999999997 16.19121 12.146619999999997 16.825380000000003 C 12.080949999999996 17.501580000000004 12.394889999999997 18.15827 12.960619999999997 18.528070000000003 C 12.886559999999998 20.06368 13.622099999999996 21.52474 14.895619999999997 22.371710000000004 L 14.895619999999997 24.957980000000003 C 14.056619999999997 25.266280000000002 13.158619999999997 25.562480000000004 12.287619999999997 25.847610000000003 C 10.565269999999996 26.341070000000002 8.894979999999997 27.003130000000002 7.300619999999997 27.824340000000003 C 2.108869999999997 22.01659 2.416819999999997 13.109000000000004 7.997079999999997 7.679280000000002 C 13.577329999999996 2.249550000000002 22.42391 2.249550000000002 28.004159999999995 7.679280000000002 C 33.58441 13.109010000000001 33.89236999999999 22.01659 28.700619999999994 27.824340000000003 z M 17.99762 2.59131 C 11.832590000000001 2.5902 6.259340000000002 6.28861 3.825960000000002 11.9956 C 1.392580000000002 17.70259 2.566750000000002 24.32136 6.811620000000002 28.825779999999998 C 9.766060000000003 31.835949999999997 13.771470000000003 33.56317 17.972620000000003 33.638639999999995 C 22.175180000000005 33.56697 26.182300000000005 31.837919999999997 29.13362 28.822759999999995 L 29.140620000000002 28.812679999999997 C 33.371210000000005 24.313029999999998 34.5427 17.71053 32.12142 12.012839999999997 C 29.70014 6.315159999999997 24.14935 2.6123999999999974 17.99762 2.591299999999997 z M 18 43 C 19.656 43 21 44.344 21 46 C 21 47.656 19.656 49 18 49 C 16.344 49 15 47.656 15 46 C 15 44.344 16.344 43 18 43 z',
                fillColor: this.googleMapOptions.proMarkerColor,
                fillOpacity: 1,
                anchor: new google.maps.Point(12.5, 43),
                strokeWeight: 0,
                scale: 1,
            },
        };

        this.googleOptionsPath = {
            suppressMarkers: true,
            polylineOptions: {
                strokeWeight: 5,
                strokeOpacity: 0.8,
                strokeColor: this.googleMapOptions.pathColor,
            },
        };

        this.googleOptionsMap = {
            styles: this.trackingStatus !== 'disable'
                ? undefined
                : grayGoogleMap,
            minZoom: 5,
            zoom: 15,
            center: this.proTrackingData.booking_address_coordinates,
            maxZoom: 15,
            zoomControl: false,
            scrollwheel: false,
            draggable: false,
            disableDoubleClickZoom: true,
            disableDefaultUI: true,
        };

        this.updateGoogleMap();

        // flag to display map
        this.googleMapLoaded = true;
    }

    public getDirectPhone(action) {
        let directPhone: any = '';
        if (action?.deep_link) {
            directPhone = this.getParameterByName('fs_payload[phone]', action.deep_link);
        }
        return directPhone;
    }

    private getParameterByName(name, params) {
        name = name.replace(/[\[\]]/g, '\\$&');
        const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
            results = regex.exec(params);
        if (!results) return null;
        if (!results[2]) return '';
        return decodeURIComponent(results[2].replace(/\+/g, ' '));
    }
}
